import { z } from "zod";
import { PaddingTypes, SpacingTypes } from "../types";

export const collectionPageSchema = z.object({
  uniqueName: z.string(),
  projectId: z.string().optional(),
  branding: z.object({
    style: z.object({
      isSquareLogo: z.boolean().default(true),
      primaryColor: z.string(),
      textColor: z.string(),
      secondaryColor: z.string(),
      backgroundColor: z.string(),
      font: z.string().optional().nullable(),
      containerPadding: z.nativeEnum(PaddingTypes),
      elementSpacing: z.nativeEnum(SpacingTypes),
    }),
    logo: z.any(),
    removeTestifyBranding: z.boolean(),
  }),
  welcomePage: z.object({
    title: z.string(),
    message: z.string(),
    showEstimatedTime: z.boolean(),
    estimatedMinutes: z.string(),
  }),
  responsePage: z.object({
    title: z.string(),
    subtitle: z.string(),
    prompt: z.string(),
    collectionTypes: z.string(),
    shouldCollectStars: z.boolean(),
    allowAttachments: z.boolean(),
    theme: z.boolean(),
  }),
  thankYouPage: z.object({
    title: z.string(),
    image: z.any(),
    message: z.string(),
    redirect: z.string(),
    allowImage: z.boolean(),
    allSocialSharing: z.boolean(),
    redirectDelay: z.number(),
    shareOn: z.object({
      facebook: z.boolean(),
      twitter: z.boolean(),
      linkedIn: z.boolean(),
      email: z.boolean(),
    }),
  }),
  labels: z.object({
    name: z.string(),
    headline: z.string(),
    email: z.string(),
    attachments: z.string(),
    photo: z.string(),
    rating: z.string(),
    link: z.string(),
    videoButton: z.string(),
    textButton: z.string(),
    submitButton: z.string(),
    consent: z.string(),
    question: z.string(),
    pauseMessage: z.string(),
  }),
  placeholder: z.object({
    name: z.string(),
    headline: z.string(),
    email: z.string(),
    attachments: z.string(),
    photo: z.string(),
    rating: z.string(),
    link: z.string(),
    consent: z.string(),
  }),
  formFields: z.object({
    name: z.object({
      enabled: z.boolean(),
      required: z.boolean(),
    }),
    email: z.object({
      enabled: z.boolean(),
      required: z.boolean(),
    }),
    headline: z.object({
      enabled: z.boolean(),
      required: z.boolean(),
    }),
    link: z.object({
      enabled: z.boolean(),
      required: z.boolean(),
    }),
    photo: z.object({
      enabled: z.boolean(),
      required: z.boolean(),
    }),
    consent: z.object({
      enabled: z.boolean(),
      required: z.boolean(),
    }),
  }),
  notifications: z.object({
    senderName: z.string(),
    address: z.string(),
    subject: z.string(),
    message: z.string(),
  }),
  settings: z.object({
    maxVideoDuration: z.string(),
    textCharacterLimit: z.number(),
    shouldAutoPopulateWall: z.boolean(),
    isFrozen: z.boolean().default(false),
  }),
  seo: z.object({
    title: z.string(),
    description: z.string(),
    keywords: z.array(z.string()),
    favicon: z.string(),
    ogImage: z.any(),
    ogTitle: z.string(),
    ogDescription: z.string(),
  }),
  domain: z.object({
    subdomain: z.string(),
    forceSSL: z.boolean(),
  }),
});

export const collectionFormObject = [
  "branding",
  "labels",
  "notifications",
  "placeholder",
  "responsePage",
  "thankYouPage",
  "welcomePage",
  "settings",
];

export interface CollectionForm extends z.infer<typeof collectionPageSchema> {
  id?: string;
  projectId?: string;
}

export const testimonialFormSchema = z.object({
  type: z.string(),
  source: z.string(),
  content: z.string().optional(),
  rating: z.number().optional(),
  media: z.array(z.any()).default([]),
  url: z.string().optional(),
  date: z.date(),
  videoUrl: z.string().optional(),
  videoDetails: z.any().optional(),
  customer: z.object({
    name: z.string(),
    email: z.string().email().optional(),
    avatar: z.any().optional(),
    title: z.string().optional(),
    url: z.string().url().optional(),
  }),
  projectId: z.string(),
  approved: z.boolean().optional().default(false),
  published: z.boolean().optional().default(false),
  avatar: z.any().optional(),
});

export type CollectionFormTypes = z.infer<typeof collectionPageSchema>;
export type TestimonialFormTypes = z.infer<typeof testimonialFormSchema>;
